import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, money, logoUrl } from "@/lib/api";

export default function PayPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const captured = useRef(false);

  const load = () => api.get(`/public/invoices/${token}`).then((r) => setInv(r.data)).catch(() => setError("Invoice not found"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const orderId = params.get("token");
    if (!orderId || params.get("cancelled") || captured.current) return;
    captured.current = true;
    setBusy(true);
    api.post(`/public/invoices/${token}/paypal/capture`, { order_id: orderId })
      .then(() => { setParams({}, { replace: true }); return load(); })
      .catch((e) => setError(errMsg(e, "Payment could not be completed")))
      .finally(() => setBusy(false));
  }, [params, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const base = `${window.location.origin}/pay/${token}`;
      const r = await api.post(`/public/invoices/${token}/paypal/order`, { return_url: base, cancel_url: `${base}?cancelled=1` });
      window.location.href = r.data.approve_url;
    } catch (e) {
      setError(errMsg(e, "Could not start PayPal checkout"));
      setBusy(false);
    }
  };

  if (!inv && !error) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  if (!inv) return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="pay-not-found">{error}</div>;

  const paid = inv.status === "paid";
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 py-12 px-4" data-testid="pay-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8 border-b border-slate-200 flex items-center justify-between gap-4">
          {inv.has_logo ? <img src={logoUrl(inv.number)} alt={inv.company_name} className="max-h-12 max-w-[180px] object-contain" /> : <span className="font-display text-xl font-bold">{inv.company_name}</span>}
          <div className="text-right"><p className="text-xs uppercase tracking-wider text-slate-500">Invoice</p><p className="num font-bold" data-testid="pay-invoice-number">{inv.number}</p></div>
        </div>
        <div className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Billed to</p><p className="font-semibold">{inv.client?.name}</p><p className="text-slate-500">{inv.client?.company}</p></div>
            <div className="text-right"><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Issued</p><p>{inv.created_at?.slice(0, 10)}</p>{inv.due_date && <p className="text-slate-500">Due {inv.due_date}</p>}</div>
          </div>
          <div className="rounded-xl border border-slate-200">
            <div className="flex justify-between gap-4 p-4 text-sm"><div><p className="font-semibold">{inv.description}</p><p className="text-slate-500">{inv.tier_label} tier{inv.study_name ? ` · ${inv.study_name}` : ""}</p></div><p className="num font-semibold">{money(inv.discount?.original_amount ?? inv.deposit_credit?.original_amount ?? inv.amount, inv.currency)}</p></div>
            {inv.discount && <div className="flex justify-between gap-4 px-4 pb-4 text-sm text-emerald-700" data-testid="pay-discount"><span>Repeat-client discount ({inv.discount.percent}%)</span><span className="num">−{money(inv.discount.original_amount * inv.discount.percent / 100, inv.currency)}</span></div>}
            {inv.deposit_credit && <div className="flex justify-between gap-4 px-4 pb-4 text-sm text-emerald-700" data-testid="pay-deposit-credit"><span>Deposit already paid</span><span className="num">−{money(inv.deposit_credit.amount, inv.currency)}</span></div>}
            <div className="flex justify-between p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl"><span className="font-semibold">Total due</span><span data-testid="pay-amount" className="num text-xl font-bold">{paid || inv.status === "void" ? money(0, inv.currency) : money(inv.amount, inv.currency)}</span></div>
          </div>
          {error && <p className="flex items-center gap-2 text-sm text-rose-600" data-testid="pay-error"><AlertTriangle className="h-4 w-4" />{error}</p>}
          {params.get("cancelled") && !paid && <p className="text-sm text-amber-600" data-testid="pay-cancelled">Payment was cancelled. You can try again.</p>}
          {paid ? (
            <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800" data-testid="pay-success"><CheckCircle2 className="h-5 w-5" />Paid{inv.paid_at ? ` on ${inv.paid_at.slice(0, 10)}` : ""}. Thank you!</div>
          ) : inv.status === "void" ? (
            <p className="text-slate-500" data-testid="pay-void">This invoice has been voided.</p>
          ) : inv.paypal_enabled ? (
            <Button data-testid="pay-paypal-btn" disabled={busy} onClick={pay} className="w-full h-12 rounded-full bg-[#ffc439] hover:bg-[#f2b92f] text-[#003087] font-bold text-base">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Pay with <span className="italic ml-1">PayPal</span></>}
            </Button>
          ) : (
            <p className="text-sm text-slate-500" data-testid="pay-unavailable">Online payment is not available yet. Please contact {inv.company_name} for payment instructions.</p>
          )}
          {inv.paypal_enabled && inv.status === "unpaid" && <p className="flex items-center justify-center gap-1 text-xs text-slate-400"><Lock className="h-3 w-3" />Secure checkout by PayPal — cards accepted, no PayPal account needed</p>}
        </div>
      </div>
    </div>
  );
}
